import type {
  GuestCommunicationChannel,
  GuestCommunicationMessageKey,
  PropertyCommunicationCatalogResponse,
  PropertyCommunicationChannelResponse,
  PropertyCommunicationContent,
  PropertyCommunicationField,
} from '@/shared/api';
import { cloneCatalog, own } from './catalogModel';
import { fieldIssues } from './fieldIssues';

export type Lang = 'De' | 'En';
export type FieldKey = 'subject' | 'heading' | 'body' | 'ctaLabel';

export interface FieldSpec {
  key: FieldKey;
  label: string;
  max: number;
  singleLine: boolean;
  /** Das Textfeld im Sinne des Servers; der Linktext kennt keine Felder. */
  apiField: PropertyCommunicationField | null;
}

// Grenzen wie in PropertyCommunicationCatalogRules.
const EMAIL: FieldSpec[] = [
  { key: 'subject', label: 'Betreff', max: 300, singleLine: true, apiField: 'Subject' },
  { key: 'heading', label: 'Überschrift', max: 300, singleLine: true, apiField: 'Heading' },
  { key: 'body', label: 'Text', max: 4000, singleLine: false, apiField: 'Body' },
  { key: 'ctaLabel', label: 'Linktext zum Gastportal', max: 200, singleLine: true, apiField: null },
];
const OTA: FieldSpec[] = [EMAIL[2]!];
// Die Danke-Mail trägt keinen Gastportal-Link – ein Linktext hätte dort keine Wirkung.
const POST_STAY_EMAIL: FieldSpec[] = EMAIL.filter((field) => field.key !== 'ctaLabel');

export const fieldsFor = (
  channel: GuestCommunicationChannel,
  key?: GuestCommunicationMessageKey,
) => (channel === 'Email' ? (key === 'PostStay' ? POST_STAY_EMAIL : EMAIL) : OTA);

export const LANGUAGE_LABEL: Record<Lang, string> = { De: 'Deutsch', En: 'Englisch' };

export const contentKey = (field: FieldKey, lang: Lang) =>
  `${field}${lang}` as keyof PropertyCommunicationContent;

export { own };

export interface Change {
  key: GuestCommunicationMessageKey;
  channel: GuestCommunicationChannel;
  lang: Lang;
  field: FieldSpec;
}

/** Jedes Feld, dessen eigener Text sich gegenüber dem gespeicherten Stand geändert hat. */
export function changes(
  draft: PropertyCommunicationCatalogResponse,
  baseline: PropertyCommunicationCatalogResponse,
): Change[] {
  return eachField(draft, (message, channel, lang, field) => {
    const saved = baseline.messages
      .find((item) => item.key === message)
      ?.channels.find((item) => item.channel === channel.channel);
    const key = contentKey(field.key, lang);
    return own(channel[key]) !== own(saved?.[key]);
  });
}

/**
 * Nach dem Speichern gilt der Serverstand — außer für Felder, die während des Speicherns weiter
 * bearbeitet wurden: die bleiben Entwurf, statt still überschrieben zu werden.
 */
export function keepLaterEdits(
  current: PropertyCommunicationCatalogResponse,
  sent: PropertyCommunicationCatalogResponse,
  saved: PropertyCommunicationCatalogResponse,
) {
  const next = cloneCatalog(saved);
  const find = (catalog: PropertyCommunicationCatalogResponse, key: string, channel: string) =>
    catalog.messages
      .find((message) => message.key === key)
      ?.channels.find((item) => item.channel === channel);
  eachField(current, (message, channel, lang, field) => {
    const key = contentKey(field.key, lang);
    const target = find(next, message, channel.channel);
    if (target && own(channel[key]) !== own(find(sent, message, channel.channel)?.[key])) {
      target[key] = channel[key];
    }
    return false;
  });
  return next;
}

export interface Issue extends Change {
  message: string;
}

/** Alles, was der Server beim Speichern ablehnen würde — nur eigene Texte, Standards sind gültig. */
export function catalogIssues(catalog: PropertyCommunicationCatalogResponse): Issue[] {
  const issues: Issue[] = [];
  eachField(catalog, (message, channel, lang, field) => {
    const [first] = fieldIssues(channel[contentKey(field.key, lang)], {
      placeholders: channel.placeholders ?? [],
      field: field.apiField,
      max: field.max,
      singleLine: field.singleLine,
    });
    if (first) issues.push({ key: message, channel: channel.channel, lang, field, message: first });
    return false;
  });
  return issues;
}

function eachField(
  catalog: PropertyCommunicationCatalogResponse,
  pick: (
    key: GuestCommunicationMessageKey,
    channel: PropertyCommunicationChannelResponse,
    lang: Lang,
    field: FieldSpec,
  ) => boolean,
): Change[] {
  const result: Change[] = [];
  for (const message of catalog.messages) {
    for (const channel of message.channels) {
      for (const field of fieldsFor(channel.channel, message.key)) {
        for (const lang of ['De', 'En'] as const) {
          if (pick(message.key, channel, lang, field)) {
            result.push({ key: message.key, channel: channel.channel, lang, field });
          }
        }
      }
    }
  }
  return result;
}
